import type { ComputedRef } from 'vue'

import type { EntryPoint } from './useShoppingAssistantContext'

type Open = (options: {
  entryPoint: EntryPoint
  entrySource?: string | null
  shouldResumeLatest?: boolean
}) => void

/**
 * Orchestrates the bare `#assistant_open` hash trigger for the web sidebar:
 * opens the assistant into the user's latest chat (or a fresh one if there is
 * none), with no seeded message.
 *
 * Unlike `useSeededSidebarLaunch`, this is a pure open: when the sidebar is
 * already open it no-ops (a bare open carries no message to inject and no
 * intent to discard the live conversation), so the `#assistant_open` key is
 * consumed one-shot without disrupting an ongoing chat.
 *
 * `trackOpen` mirrors `useSeededSidebarLaunch`'s `trackSeededOpen` so both
 * hash entry points report `ai_shopping_assistant_opened` under the
 * `seeded_link` zone, with `entrySource` attributed from
 * `#assistant_message_source` when present. Page context flows in reactively
 * via `useShoppingAssistantBrowsingContext` once the chat mounts, the same as
 * the seeded-message hash path.
 */
export function useOpenTriggerLaunch({
  isOpenTrigger,
  entrySource,
  isOpen,
  open,
  trackOpen,
}: {
  isOpenTrigger: ComputedRef<boolean>
  entrySource: ComputedRef<string>
  isOpen: ComputedRef<boolean>
  open: Open
  trackOpen: () => void
}) {
  /**
   * Runs the open trigger when the route carries `#assistant_open`. Returns
   * `true` when it handled the trigger (so the caller strips the hash key
   * regardless of outcome), `false` otherwise.
   */
  function maybeLaunch(): boolean {
    if (!isOpenTrigger.value) {
      return false
    }

    // Already open: nothing to do — the bare trigger carries no message and
    // no intent to replace the live conversation. The hash key is still
    // consumed by the caller so a refresh doesn't replay it.
    if (isOpen.value) {
      return true
    }

    open({
      entryPoint: 'seeded_link',
      entrySource: entrySource.value || null,
      // Resume the latest server-side chat when one exists, mirroring the
      // un-seeded FAB reopen rather than forcing a fresh conversation.
      shouldResumeLatest: true,
    })
    trackOpen()

    return true
  }

  return {
    maybeLaunch,
  }
}
